import { type CallHomeUser } from '@solinkcorp/authentication';
import { toast } from '@solinkcorp/solink-components';
import { AppDispatcher } from 'dispatcher';
import AuthService from 'models/AuthService';
import { BaseStore } from 'stores/BaseStore';
import LocaleUtil from 'utils/LocaleUtil';
import {
  ACCEPT_EULA,
  APPEND_USERS,
  CLEAR_CURRENT_USER,
  FAST_NOTIFICATION_DURATION,
  FETCH_CURRENT_USER_ERROR,
  FETCH_CURRENT_USER_SUCCESS,
  GET_DIGEST_SETTINGS,
  GET_REAL_TIME_SETTINGS,
  GET_USERS_ERROR,
  RECV_USERS,
  UPDATE_CURRENT_USER_ERROR,
  UPDATE_CURRENT_USER_SUCCESS,
  UPDATE_DIGEST_SETTINGS,
  UPDATE_DIGEST_SETTINGS_ERROR,
  UPDATE_MFA,
  UPDATE_REAL_TIME_SETTINGS_ERROR,
  UPDATE_REAL_TIME_SETTINGS_SUCCESS,
  USER_ADDED,
  USER_ADDED_ERROR,
  USER_REMOVED,
  USER_REMOVED_ERROR,
} from 'vd-constants';
import { getReduxStore } from './store';

let _authUser: CallHomeUser | undefined;
let _currentUser: CallHomeUser | undefined;
let _digestSettings: any = {};
let _realTimeNotifications: { smsId?: string; smsNumber?: string } = {};
let _users: { [userId: string]: CallHomeUser } = {};

function parseUsers(usersData: CallHomeUser[] | undefined) {
  if (Array.isArray(usersData)) {
    usersData.forEach((user) => {
      _users[user.id] = user;
    });
  }
}

const UserStore = {
  ...BaseStore,
  getUsers: function () {
    return Object.keys(_users).map((key) => _users[key]);
  },

  getDigestSettings: function () {
    return _digestSettings;
  },

  getRealTimeNotificationBindings: function () {
    return _realTimeNotifications;
  },

  getSupportAccessState: function () {
    return _authUser?.isSupportAccessEnabled;
  },

  getSupportAccessExpiresAt: function () {
    return _authUser?.supportAccessExpiresAt;
  },

  getIsDigestEnabled: function () {
    return _authUser?.isDigestEnabled;
  },

  getCurrentUser: function (): CallHomeUser | undefined {
    return _currentUser;
  },

  dispatchToken: AppDispatcher.register((action: any) => {
    const localeCode = getReduxStore().getState().localeState.localeCode;
    const locale = LocaleUtil.getLocale(localeCode);
    switch (action.type) {
      case RECV_USERS:
        _users = {};

        parseUsers(action.payload);

        UserStore.emitChange();
        break;

      case APPEND_USERS:
        parseUsers(action.payload);

        UserStore.emitChange();
        break;

      case GET_USERS_ERROR:
        toast.error(locale.error.unableToGetUsers, {
          autoClose: FAST_NOTIFICATION_DURATION,
        });
        break;

      case USER_ADDED:
        _users[action.data.id] = action.data;

        toast.success(locale.usersPage.addSuccess, {
          autoClose: FAST_NOTIFICATION_DURATION,
        });

        UserStore.emitChange();
        break;

      case USER_REMOVED:
        delete _users[action.id];

        toast.success(locale.usersPage.deleteSuccess, {
          autoClose: FAST_NOTIFICATION_DURATION,
        });

        UserStore.emitChange();
        break;

      case USER_REMOVED_ERROR:
        toast.error(locale.error.unableToDeleteUser, {
          autoClose: FAST_NOTIFICATION_DURATION,
        });
        break;

      case USER_ADDED_ERROR:
        toast.error(locale.error.unableToAddUser, {
          autoClose: FAST_NOTIFICATION_DURATION,
        });
        break;

      case GET_DIGEST_SETTINGS:
        _digestSettings = action.payload;

        UserStore.emitChange();
        break;

      case UPDATE_DIGEST_SETTINGS:
        toast.success(locale.error.userUpdateSuccess, {
          autoClose: FAST_NOTIFICATION_DURATION,
        });
        _digestSettings = action.payload;

        UserStore.emitChange();
        break;

      case UPDATE_DIGEST_SETTINGS_ERROR:
        toast.error(locale.error.userUpdateError, {
          autoClose: FAST_NOTIFICATION_DURATION,
        });
        break;

      case GET_REAL_TIME_SETTINGS: {
        const notifications = action.payload;
        for (const notification of notifications) {
          if (notification.bindingType === 'sms') {
            _realTimeNotifications.smsId = notification.sid;
            _realTimeNotifications.smsNumber = notification.address;
            break;
          }
        }

        UserStore.emitChange();
        break;
      }

      case UPDATE_REAL_TIME_SETTINGS_SUCCESS: {
        toast.success(locale.error.userUpdateSuccess, {
          autoClose: FAST_NOTIFICATION_DURATION,
        });

        const notification = action.smsBinding || action.payload || {};
        _realTimeNotifications = {
          ..._realTimeNotifications,
          smsId: notification.sid,
          smsNumber: notification.address,
        };
        UserStore.emitChange();
        break;
      }

      case UPDATE_REAL_TIME_SETTINGS_ERROR: {
        toast.error(locale.error.userUpdateError, {
          autoClose: FAST_NOTIFICATION_DURATION,
        });
        break;
      }

      case FETCH_CURRENT_USER_SUCCESS: {
        const { payload } = action;
        const user: CallHomeUser = payload;
        const { id } = user;
        const authUserId = AuthService.getSession()?.userId;

        if (id === authUserId) {
          _authUser = user;
        }

        _currentUser = user;

        if (id) {
          _users[id] = user;
        }

        UserStore.emitChange();
        break;
      }

      case UPDATE_CURRENT_USER_SUCCESS: {
        const { payload, showNotification = true } = action;
        const { id, email } = payload;
        const authUserId = AuthService.getSession()?.userId;

        if (id === authUserId) {
          _authUser = payload;
        }

        if (showNotification) {
          toast.success(
            LocaleUtil.getLocalizedStr({
              string: locale.error.certainUserUpdateSuccess,
              replacements: { email },
            }),
            { autoClose: FAST_NOTIFICATION_DURATION },
          );
        }

        if (id === _currentUser?.id) {
          _currentUser = payload;
        }

        _users[id] = payload;
        UserStore.emitChange();
        break;
      }

      case CLEAR_CURRENT_USER: {
        _currentUser = undefined;
        UserStore.emitChange();
        break;
      }

      case FETCH_CURRENT_USER_ERROR:
      case UPDATE_CURRENT_USER_ERROR: {
        toast.error(locale.error.userUpdateError, {
          autoClose: FAST_NOTIFICATION_DURATION,
        });
        break;
      }

      case ACCEPT_EULA: {
        const {
          payload: { eulaAcceptedVersion, eulaAcceptedAt },
        } = action;

        if (_currentUser) {
          _currentUser = { ..._currentUser, eulaAcceptedVersion, eulaAcceptedAt };
          UserStore.emitChange();
        }
        break;
      }

      case UPDATE_MFA: {
        const {
          payload: { isMfaEnabled },
        } = action;
        if (_currentUser) {
          _currentUser = { ..._currentUser, isMfaEnabled };
          UserStore.emitChange();
        }
        break;
      }
    }
  }),
};

export default UserStore;
